import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader } from '../components/common';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, TextField, MenuItem, CircularProgress, Alert, Typography,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import { Pause, PlayArrow, Cancel, Search } from '@mui/icons-material';
import { subscriptionService, type CountySubscription, type SubscriptionAction } from '../services/subscription.service';
import { useSnackbar } from '../contexts/SnackbarContext';
import { usePermission } from '../hooks';
import { colors } from '../theme/theme';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  active: 'success',
  suspended: 'warning',
  cancelled: 'default',
};

const fmt = (n?: number) => `KES ${(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const SubscriptionsPage = () => {
  const { can } = usePermission();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [confirm, setConfirm] = useState<{ sub: CountySubscription; action: SubscriptionAction } | null>(null);

  const canManage = can('customers.manage') || can('customers.view');

  const { data, isLoading, error } = useQuery({
    queryKey: ['county-subscriptions', { search, status }],
    queryFn: () => subscriptionService.getSubscriptions({ search: search || undefined, status: status || undefined, per_page: 50 }),
  });

  const mutation = useMutation({
    mutationFn: ({ id, action }: { id: number; action: SubscriptionAction }) =>
      subscriptionService.updateStatus(id, action),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['county-subscriptions'] });
      const verb = vars.action === 'cancel' ? 'cancelled' : vars.action === 'pause' ? 'paused' : 'resumed';
      showSnackbar(`Subscription ${verb}`, 'success');
      setConfirm(null);
    },
    onError: (e: any) => showSnackbar(e?.response?.data?.message || 'Failed to update subscription', 'error'),
  });

  const subscriptions = data?.data ?? [];

  const runAction = (sub: CountySubscription, action: SubscriptionAction) => {
    // Cancel is destructive — confirm. Pause/resume apply directly.
    if (action === 'cancel') {
      setConfirm({ sub, action });
    } else {
      mutation.mutate({ id: sub.id, action });
    }
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Subscriptions"
          subtitle="Recurring citizen subscriptions (e.g. annual permits). Pause, resume or cancel renewal billing."
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Customers', path: '/customers' },
            { label: 'Subscriptions' },
          ]}
        />

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search customer, service or business"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: colors.grey[500] }} fontSize="small" /> }}
            sx={{ minWidth: 320 }}
          />
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="suspended">Paused</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>Failed to load subscriptions.</Alert>}

        <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: colors.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Service / Business</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cycle</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Next payment</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((s) => {
                    const isPaused = s.status === 'suspended';
                    const isCancelled = s.status === 'cancelled';
                    const rowBusy = mutation.isPending && mutation.variables?.id === s.id;
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{s.customer_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.customer_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{s.service_name}</Typography>
                          {s.asset_name && <Typography variant="caption" color="text.secondary">{s.asset_name}</Typography>}
                        </TableCell>
                        <TableCell>{s.billing_cycle || '—'}</TableCell>
                        <TableCell>{fmt(s.amount)}</TableCell>
                        <TableCell>
                          {s.next_payment_date || '—'}
                          {s.is_overdue && <Chip label="Overdue" color="error" size="small" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isPaused ? 'Paused' : s.status}
                            color={STATUS_COLOR[s.status] ?? 'default'}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {isCancelled ? (
                            <Typography variant="caption" color="text.secondary">No actions</Typography>
                          ) : canManage ? (
                            <>
                              {isPaused ? (
                                <Tooltip title="Resume renewals">
                                  <span><IconButton size="small" disabled={rowBusy} sx={{ color: colors.success.main }}
                                    onClick={() => runAction(s, 'resume')}><PlayArrow fontSize="small" /></IconButton></span>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Pause renewals">
                                  <span><IconButton size="small" disabled={rowBusy} sx={{ color: colors.warning.main }}
                                    onClick={() => runAction(s, 'pause')}><Pause fontSize="small" /></IconButton></span>
                                </Tooltip>
                              )}
                              <Tooltip title="Cancel subscription">
                                <span><IconButton size="small" color="error" disabled={rowBusy}
                                  onClick={() => runAction(s, 'cancel')}><Cancel fontSize="small" /></IconButton></span>
                              </Tooltip>
                            </>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {subscriptions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No subscriptions found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Cancel subscription?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Cancel <strong>{confirm?.sub.service_name}</strong> for <strong>{confirm?.sub.customer_name}</strong>?
              No further renewal bills will be generated. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)}>Keep it</Button>
            <Button
              variant="contained"
              color="error"
              disabled={mutation.isPending}
              onClick={() => confirm && mutation.mutate({ id: confirm.sub.id, action: 'cancel' })}
            >
              Cancel subscription
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
